import React from 'react'
import {Project, Optional} from 'data/types'
import services from 'services'
import Spinner from 'components/Spinner'
import Select from 'components/Select'
import i18n from 'i18n'
import selector from 'utils/selector'
import APIService from 'services/APIService'
import ProjectsService from 'services/ProjectsService'

export type CurrentProjectContext = {
  projects: Project[]
  project: Optional<Project>
  isLoading: boolean
  service: ProjectsService
  selectProject: (project: Optional<Project>) => void
}

type ProjectContextProps = {
  children: (context: CurrentProjectContext) => React.ReactNode
}

const selectedProjectIdSelector = (props: {id: Optional<string>}) => props.id
const projectsSelector = (props: {projects: Project[]}) => props.projects
const selectedProjectSelector = selector(selectedProjectIdSelector, projectsSelector, (selectedProjectId, projects) => {
  if (!selectedProjectId) {
    return null
  }
  return projects.find((p) => p.id.toString() === selectedProjectId)
})

export default function ProjectContext(props: ProjectContextProps) {
  const projectsService = services.getProjectsService()
  const [projects, isLoading] = projectsService.projects.hook()
  const [selectedProject, isLoadingSelectedProject] = projectsService.selectedProject.hook()

  return (
    <React.Fragment>
      {props.children({
        selectProject: (project) => {
          services.router.selectProject(project?.id)
          projectsService.selectedProject.publish(project)
          APIService.setSelectedProject(project?.id)
        },
        project: selectedProjectSelector({id: selectedProject?.id.toString(), projects}),
        projects,
        service: projectsService,
        isLoading: isLoading || isLoadingSelectedProject,
      })}
    </React.Fragment>
  )
}

ProjectContext.Selector = (props: {context: CurrentProjectContext; className?: string; onProjectSelected?: (p: Optional<Project>) => void}) => {
  return (
    <React.Fragment>
      <Select
        value={props.context.project?.id ?? -1}
        placeholder={i18n.t('projectContext.placeholder')}
        className={props.className}
        options={props.context.projects.map((p) => ({value: p.id, text: p.name}))}
        onChange={(projectId) => {
          const project = props.context.projects.find((p) => p.id.toString() === projectId)
          props.context.selectProject(project)
          props.onProjectSelected?.(project)
        }}
      />
      {props.context.isLoading && (
        <div className="pl-2">
          <Spinner />
        </div>
      )}
    </React.Fragment>
  )
}
